import type { BillingComponents } from "@steelyard/api-client";
import { billing, unwrap, ApiError } from "../api";
import type { TransportField } from "@steelyard/theme";
import { dispatchPath } from "../paths";
import { invoiceByKey } from "./invoicing";
import { getDispatchView } from "./ordering";

/** Ported from apps/admin/app/(app)/dispatch/{dispatch-state,actions,[invoiceId]/page,[invoiceId]/capture}.tsx, through the generated billing client. */

export const TRANSPORT_FIELDS: readonly TransportField[] = [
  "transporterName",
  "transporterId",
  "vehicleNumber",
  "modeOfTransport",
  "distanceKm",
  "fromPinCode",
  "toPinCode",
];

/** Billing sets these itself and ignores what is sent, so nobody is asked to type them. */
const BILLING_SET: readonly TransportField[] = ["fromPinCode", "toPinCode"];

/** The From/To pincodes Billing will record, as the order's dispatch view shows them (`fromPincode`, `shippingPincode`). */
export type DispatchPincodes = { fromPincode: string | null; toPincode: string | null };

// ponytail: billing.json types modeOfTransport as a bare string with no enum, so this list can't
// be generated. Delete it and read the generated enum the day the document publishes one.
export const TRANSPORT_MODES: readonly { value: string; label: string }[] = [
  { value: "road", label: "Road" },
  { value: "rail", label: "Rail" },
  { value: "air", label: "Air" },
  { value: "ship", label: "Ship" },
];

export type DispatchState = {
  values: Partial<Record<TransportField, string>>;
  missing: readonly TransportField[];
  persisted: boolean;
  error: unknown;
  generated: { number: string | null; status: string } | null;
};

export const NOTHING_SUBMITTED: DispatchState = { values: {}, missing: [], persisted: false, error: null, generated: null };

export type DispatchInvoice = { id: string; invoiceNumber: string; invoiceDate: string; recipientLegalName: string; state: string; orderId?: string | null; orderNumber?: string | null };
export type EWayStatus = { ewaybillStatus: string; ewaybillNo: string | null };
export type AuditRow = { id: string; action: string; reason: string | null; occurredAt: string };

export type DispatchData = {
  invoiceNotFound: boolean;
  invoice: DispatchInvoice | null;
  status: EWayStatus | null;
  audit: readonly AuditRow[];
  auditError: unknown;
  /** The order's delivery type; null when it has no order or the read failed. Third-party asks for no transport details. */
  deliveryType?: string | null;
  /** The From/To pincodes Billing will record; null when the view could not be read (Billing still sets them on save). */
  pincodes?: DispatchPincodes | null;
};

/**
 * `key` is the route's invoice number; a legacy id throws a redirect to `/dispatch/{number}`, query
 * kept. An unknown number is `invoiceNotFound`, not a failure; an ambiguous one (409) is thrown.
 */
export async function getDispatchData(request: Request, key: string): Promise<DispatchData> {
  let invoice: DispatchInvoice;
  try {
    invoice = await invoiceByKey(request, key, dispatchPath);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { invoiceNotFound: true, invoice: null, status: null, audit: [], auditError: null };
    }
    throw err;
  }
  const invoiceId = invoice.id;
  const [statusResult, auditResult, viewResult] = await Promise.allSettled([
    unwrap(
      (await billing()).GET("/api/billing/registration/status", { params: { query: { invoiceId } } }),
    ) as Promise<EWayStatus>,
    unwrap((await billing()).GET("/api/billing/admin/invoices/{id}/audit", { params: { path: { id: invoiceId } } })) as Promise<readonly AuditRow[]>,
    invoice.orderId ? getDispatchView(invoice.orderId) : Promise.resolve(null),
  ]);
  const view = viewResult.status === "fulfilled" ? viewResult.value : null;

  if (statusResult.status === "rejected") throw statusResult.reason;

  return {
    invoiceNotFound: false,
    invoice,
    status: statusResult.value ?? null,
    audit: auditResult.status === "fulfilled" ? (auditResult.value ?? []) : [],
    auditError: auditResult.status === "rejected" ? auditResult.reason : null,
    deliveryType: view?.deliveryType ?? null,
    pincodes: view ? { fromPincode: view.fromPincode ?? null, toPincode: view.shippingPincode } : null,
  };
}

function readFields(formData: FormData): Partial<Record<TransportField, string>> {
  return Object.fromEntries(TRANSPORT_FIELDS.map((field) => [field, String(formData.get(field) ?? "").trim()])) as Partial<Record<TransportField, string>>;
}

type TransportPersisted = {
  transporterName: string;
  transporterId: string;
  vehicleNumber: string;
  modeOfTransport: string;
  distanceKm: number | string;
  fromPincode: string;
  toPincode: string;
};

type EWayBillResponse = { ewaybillNo: string | null; ewaybillStatus: string };

/**
 * Two calls, in this order: persist the seven fields, then generate the bill against a system we
 * don't control. FR-002: emptiness is checked and refused before any call — no format/business
 * validation beyond that (formats vary by state and are the API's to reject).
 */
export async function generateEWayBill(invoiceId: string, formData: FormData): Promise<DispatchState> {
  const values = readFields(formData);
  const missing = TRANSPORT_FIELDS.filter((field) => !values[field] && !BILLING_SET.includes(field));
  if (missing.length > 0) return { values, missing, persisted: false, error: null, generated: null };

  let persistedValues: Partial<Record<TransportField, string>>;
  try {
    const transport = (await unwrap(
      (await billing()).POST("/api/billing/dispatch/{invoiceId}/transport", {
        params: { path: { invoiceId } },
        body: {
          transporterName: values.transporterName ?? null,
          transporterId: values.transporterId ?? null,
          vehicleNumber: values.vehicleNumber ?? null,
          // The document types a number (Billing still accepts "118" on input); an unparseable entry
          // goes as null, which the server refuses by name like any missing field.
          distanceKm: values.distanceKm ? Number(values.distanceKm) || null : null,
          modeOfTransport: values.modeOfTransport ?? null,
          fromPincode: values.fromPinCode ?? null,
          toPincode: values.toPinCode ?? null,
        },
      }),
    )) as TransportPersisted;
    persistedValues = transport
      ? {
          transporterName: transport.transporterName,
          transporterId: transport.transporterId,
          vehicleNumber: transport.vehicleNumber,
          modeOfTransport: transport.modeOfTransport,
          distanceKm: String(transport.distanceKm),
          fromPinCode: transport.fromPincode,
          toPinCode: transport.toPincode,
        }
      : values;
  } catch (error) {
    return { values, missing: [], persisted: false, error, generated: null };
  }

  try {
    const bill = (await unwrap(
      (await billing()).POST("/api/billing/dispatch/{invoiceId}/eway-bill", { params: { path: { invoiceId } } }),
    )) as EWayBillResponse;
    return {
      values: persistedValues,
      missing: [],
      persisted: true,
      error: null,
      generated: bill ? { number: bill.ewaybillNo, status: bill.ewaybillStatus } : null,
    };
  } catch (error) {
    // SC-002: the seven fields are on the server and in this state. Pressing the button again is
    // the entire remedy — details are never re-typed after this point.
    return { values: persistedValues, missing: [], persisted: true, error, generated: null };
  }
}

export async function updatePartB(invoiceId: string, newVehicleNumber: string, reason: string): Promise<void> {
  await unwrap(
    (await billing()).PUT("/api/billing/dispatch/{invoiceId}/eway-bill/part-b", {
      params: { path: { invoiceId } },
      body: { newVehicleNumber, reason },
    }),
  );
}

export async function cancelEWayBill(invoiceId: string, reason: string): Promise<void> {
  await unwrap(
    (await billing()).DELETE("/api/billing/dispatch/{invoiceId}/eway-bill", {
      params: { path: { invoiceId } },
      body: { reason },
    }),
  );
}

export type TransportRecord = BillingComponents["schemas"]["TransportRequest"];

/**
 * Saves the transport record alone — the persist half of `generateEWayBill`, without the GSP call.
 * A Third-Party handover needs it whole, docket number and contact phone included.
 */
export async function saveTransport(invoiceId: string, transport: TransportRecord): Promise<void> {
  await unwrap((await billing()).POST("/api/billing/dispatch/{invoiceId}/transport", { params: { path: { invoiceId } }, body: transport }));
}
